import { useEffect, useState } from 'react';
import { ProductType } from '@dtp/membership-service-types';
import { ProductID } from '@lib/ProductID';
import { getProductVariant } from '@lib/getProductVariant';
import useSelector from '@redux/typedHooks';
import { NAFGTMEvent } from '../../../../../types/GTM/NAFGTMEvent';
import { funnelPayload } from '../../../../../types/gtmFunnel';
import useSendEspialCheckoutFunnelEventOnce from '../../../hooks/Espial/useSendEspialCheckoutFunnelEventOnce';
import { createEventPayload, useSendGTMEventOnce } from '../../../hooks/GTM/useSendGTMEventOnce';
import { MembershipFormType } from '../../../../../types/membershipFormType';
import { useDebouncedProductId } from './useDebouncedProductId';
import { getPromotionDataFromSessionStorage } from '../../../hooks/useTrackPromotion';
import {
  didUseVippsMembershipRegistrationMethod,
  getVippsMembershipRegistrationMethodTrackingPayload,
} from '../components/VippsModal/VippsModal.utils';

const getCheckoutName = (productId?: string) => {
  if (productId === ProductID.Young || productId === ProductID.YoungWithRoadsideAssistance) return 'bli-medlem-ung';
  if (productId === ProductID.MC || productId === ProductID.MCWithRoadsideAssistance) return 'bli-medlem-mc';
  if (productId === ProductID.MembershipWithRoadsideAssistance || productId === ProductID.Membership)
    return 'bli-medlem-medlemskap';
  return 'bli-medlem-medlemskap';
};

export const useTrackingStep1 = (
  product: ProductType,
  membershipForm: Partial<MembershipFormType>,
  addonProductsPrice: any,
  mainProductPrice: number,
  numberOfAdditionalMembers: number,
  didUseVipps?: boolean,
) => {
  const CHECKOUT_NAME = getCheckoutName(membershipForm?.product?.productId);
  const CHECKOUT_STEP = 1;
  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);
  const promotionData = getPromotionDataFromSessionStorage();
  const checkoutStepName = numberOfAdditionalMembers === 0 ? `${CHECKOUT_STEP} - Om meg` : `${CHECKOUT_STEP} - Om oss`;
  const [beginCheckoutTracked, setBeginCheckoutTracked] = useState(false);
  const [addShippingInfoTracked, setAddShippingInfoTracked] = useState(false);
  const registrationMethodPayload = getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps);
  const beginCheckoutPayload =
    didUseVipps !== undefined &&
    product &&
    membershipForm?.product?.productId &&
    createEventPayload({
      event: NAFGTMEvent.beginCheckout,
      ...registrationMethodPayload,
      ...(campaignCode && { campaign_code: campaignCode }),
      ecommerce: {
        items: [
          {
            item_id: membershipForm.product.productId,
            quantity: '1',
            item_name: product.productName,
            price: product.productAppPrice[1]?.itemTotalPrice.toFixed(2),
            item_category2: 'Medlemskap',
            item_category3: 'Innmelding',
            item_variant: getProductVariant(membershipForm.product.productId),
            ...(promotionData ?? {}),
          },
        ],
      },
    });
  const addShippingInfoPayload =
    didUseVipps !== undefined &&
    product &&
    membershipForm?.product?.amount &&
    createEventPayload({
      event: NAFGTMEvent.addShippingInfo,
      ...registrationMethodPayload,
      ...(campaignCode && { campaign_code: campaignCode }),
      ecommerce: {
        value: addonProductsPrice + mainProductPrice,
        items: [
          {
            item_id: product.productNumber,
            quantity: membershipForm?.product?.amount,
            item_name: product.productName,
            price:
              membershipForm &&
              membershipForm.product &&
              (
                product.productAppPrice[membershipForm?.product?.amount].itemTotalPrice / membershipForm.product.amount
              ).toFixed(2),
            item_category2: 'Medlemskap',
            item_category3: 'Innmelding',
            item_variant: getProductVariant(product.productNumber),
            ...(promotionData ?? {}),
          },
        ],
      },
    });
  const checkoutFunnelPayload = didUseVipps !== undefined &&
    membershipForm?.product?.productId && {
      ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
      ...registrationMethodPayload,
      ...(campaignCode && { campaign_code: campaignCode }),
    };
  const espialPayload = didUseVipps !== undefined &&
    membershipForm?.product?.productId && [
      { checkout_name: CHECKOUT_NAME },
      { checkout_step: CHECKOUT_STEP },
      { checkout_step_name: checkoutStepName },
      { checkout_product: membershipForm.product.productId },
      registrationMethodPayload,
    ];

  useSendGTMEventOnce(
    beginCheckoutPayload,
    [product, membershipForm?.product?.productId, didUseVipps, campaignCode],
    () => setBeginCheckoutTracked(true),
  );

  useSendGTMEventOnce(
    beginCheckoutTracked ? addShippingInfoPayload : undefined,
    [product, membershipForm?.product?.amount, didUseVipps, campaignCode, beginCheckoutTracked],
    () => setAddShippingInfoTracked(true),
  );

  useSendGTMEventOnce(addShippingInfoTracked ? checkoutFunnelPayload : undefined, [
    membershipForm?.product?.productId,
    membershipForm?.product?.amount,
    didUseVipps,
    campaignCode,
    addShippingInfoTracked,
  ]);

  useSendEspialCheckoutFunnelEventOnce(addShippingInfoTracked ? espialPayload : undefined, [
    membershipForm?.product?.productId,
    didUseVipps,
    addShippingInfoTracked,
  ]);
};

export const useTrackingStep2 = (membershipForm: Partial<MembershipFormType>) => {
  const CHECKOUT_NAME = getCheckoutName(membershipForm?.product?.productId);
  const CHECKOUT_STEP = 2;
  const checkoutStepName = `${CHECKOUT_STEP} - Tilleggsvalg`;
  const didUseVipps = didUseVippsMembershipRegistrationMethod();
  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);
  const checkoutFunnelPayload = membershipForm?.product?.productId && {
    ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
    ...getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
    ...(campaignCode && { campaign_code: campaignCode }),
  };
  const espialPayload = membershipForm?.product?.productId && [
    { checkout_name: CHECKOUT_NAME },
    { checkout_step: CHECKOUT_STEP },
    { checkout_step_name: checkoutStepName },
    { checkout_product: membershipForm.product.productId },
    getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
  ];

  useSendGTMEventOnce(checkoutFunnelPayload, [
    membershipForm?.product?.productId,
    membershipForm?.product?.amount,
    didUseVipps,
  ]);

  useSendEspialCheckoutFunnelEventOnce(espialPayload, [membershipForm?.product?.productId, didUseVipps]);
};

export const useTrackingStep3 = (membershipForm: Partial<MembershipFormType>) => {
  const CHECKOUT_NAME = getCheckoutName(membershipForm?.product?.productId);
  const CHECKOUT_STEP = 3;
  const checkoutStepName = `${CHECKOUT_STEP} - Samtykker`;
  const didUseVipps = didUseVippsMembershipRegistrationMethod();
  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);
  const checkoutFunnelPayload = membershipForm?.product?.productId && {
    ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
    ...getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
    ...(campaignCode && { campaign_code: campaignCode }),
  };
  const espialPayload = membershipForm?.product?.productId && [
    { checkout_name: CHECKOUT_NAME },
    { checkout_step: CHECKOUT_STEP },
    { checkout_step_name: checkoutStepName },
    { checkout_product: membershipForm.product.productId },
    getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
  ];

  useSendGTMEventOnce(checkoutFunnelPayload, [
    membershipForm?.product?.productId,
    membershipForm?.product?.amount,
    didUseVipps,
  ]);

  useSendEspialCheckoutFunnelEventOnce(espialPayload, [membershipForm?.product?.productId, didUseVipps]);
};

export const useTrackingStep4 = (productNumber?: string) => {
  const debouncedProductNumber = useDebouncedProductId(productNumber, 300);
  const CHECKOUT_NAME = getCheckoutName(debouncedProductNumber);
  const CHECKOUT_STEP = 4;
  const checkoutStepName = `${CHECKOUT_STEP} - Betaling`;
  const didUseVipps = didUseVippsMembershipRegistrationMethod();
  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);
  const [status, setStatus] = useState<'pending' | 'done' | 'error'>('pending');
  const [gtmDone, setGtmDone] = useState(false);
  const [espialDone, setEspialDone] = useState(false);
  const checkoutFunnelPayload = debouncedProductNumber && {
    ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
    ...getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
    ...(campaignCode && { campaign_code: campaignCode }),
  };
  const espialPayload = debouncedProductNumber && [
    { checkout_name: CHECKOUT_NAME },
    { checkout_step: CHECKOUT_STEP },
    { checkout_step_name: checkoutStepName },
    { checkout_product: debouncedProductNumber },
    getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
  ];

  useSendGTMEventOnce(
    checkoutFunnelPayload,
    [],
    () => setGtmDone(true),
    () => setStatus('error'),
  );

  useSendEspialCheckoutFunnelEventOnce(
    espialPayload,
    [debouncedProductNumber, didUseVipps],
    () => setEspialDone(true),
    () => setStatus('error'),
  );

  useEffect(() => {
    if (gtmDone && espialDone && status === 'pending') {
      setStatus('done');
    }
  }, [gtmDone, espialDone, status]);

  return status;
};

export const useTrackingStep5 = (productNumber?: string, campaignCode?: string) => {
  const debouncedProductNumber = useDebouncedProductId(productNumber, 300);
  const CHECKOUT_NAME = getCheckoutName(debouncedProductNumber);
  const CHECKOUT_STEP = 5;
  const checkoutStepName = `${CHECKOUT_STEP} - Kvittering`;
  const didUseVipps = didUseVippsMembershipRegistrationMethod();
  const [status, setStatus] = useState<'pending' | 'done' | 'error'>('pending');
  const [gtmDone, setGtmDone] = useState(false);
  const [espialDone, setEspialDone] = useState(false);
  const checkoutFunnelPayload = debouncedProductNumber && {
    ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
    ...getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
    ...(campaignCode && { campaign_code: campaignCode }),
  };
  const espialPayload = debouncedProductNumber && [
    { checkout_name: CHECKOUT_NAME },
    { checkout_step: CHECKOUT_STEP },
    { checkout_step_name: checkoutStepName },
    { checkout_product: debouncedProductNumber },
    getVippsMembershipRegistrationMethodTrackingPayload(didUseVipps),
  ];

  useSendGTMEventOnce(
    checkoutFunnelPayload,
    [],
    () => setGtmDone(true),
    () => setStatus('error'),
  );

  useSendEspialCheckoutFunnelEventOnce(
    espialPayload,
    [debouncedProductNumber, didUseVipps],
    () => setEspialDone(true),
    () => setStatus('error'),
  );

  useEffect(() => {
    if (gtmDone && espialDone && status === 'pending') {
      setStatus('done');
    }
  }, [gtmDone, espialDone, status]);

  return status;
};
